import * as React from 'react';
import { EgTnlBucket } from '@expediagroup/egtnl-bucket-web';
import { getExperimentLogger } from 'bernie-core';
import { ExperimentProvider, useExperiment } from '@shared-ui/experiment-context';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol */

function __read(o, n) {
  var m = typeof Symbol === "function" && o[Symbol.iterator];
  if (!m) return o;
  var i = m.call(o),
    r,
    ar = [],
    e;
  try {
    while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
  } catch (error) {
    e = {
      error: error
    };
  } finally {
    try {
      if (r && !r.done && (m = i["return"])) m.call(i);
    } finally {
      if (e) throw e.error;
    }
  }
  return ar;
}
typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
  var e = new Error(message);
  return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};
var AbacusExperimentStoreDelegate = /** @class */function () {
  function AbacusExperimentStoreDelegate(exposureData, logger) {
    this.exposureData = exposureData;
    this.logger = logger;
    this.getExposure = this.getExposure.bind(this);
    this.logExperiment = this.logExperiment.bind(this);
    this.serialize = this.serialize.bind(this);
  }
  Object.defineProperty(AbacusExperimentStoreDelegate.prototype, "exposures", {
    get: function () {
      return this.exposureData;
    },
    enumerable: false,
    configurable: true
  });
  AbacusExperimentStoreDelegate.prototype.getExposure = function (name) {
    var _a;
    if ((_a = this.exposureData) === null || _a === void 0 ? void 0 : _a.exposures) {
      return this.exposureData.exposures[name];
    }
    return null;
  };
  AbacusExperimentStoreDelegate.prototype.logExperiment = function (exposure) {
    this.logger.logExperiment(exposure);
  };
  AbacusExperimentStoreDelegate.prototype.serialize = function () {
    var _a;
    var exposures = Object.entries(((_a = this.exposureData) === null || _a === void 0 ? void 0 : _a.exposures) || {}).reduce(function (acc, _a) {
      var _b = __read(_a, 2),
        expName = _b[0],
        exposure = _b[1];
      acc[expName] = {
        name: exposure.name,
        bucket: exposure.bucket
      };
      return acc;
    }, {});
    return {
      exposures: exposures
    };
  };
  return AbacusExperimentStoreDelegate;
}();
var EgtnlBrowserDelegate = /** @class */function () {
  function EgtnlBrowserDelegate(evaluator, context, pageData, exposureData) {
    this.evaluator = evaluator;
    this.context = context;
    this.pageData = pageData;
    this.exposureData = exposureData;
    this.getExposure = this.getExposure.bind(this);
    this.logExperiment = this.logExperiment.bind(this);
  }
  Object.defineProperty(EgtnlBrowserDelegate.prototype, "exposures", {
    get: function () {
      return this.exposureData;
    },
    enumerable: false,
    configurable: true
  });
  EgtnlBrowserDelegate.prototype.getExposure = function (name) {
    var _a, _b;
    if ((_b = (_a = this.exposureData) === null || _a === void 0 ? void 0 : _a.exposures) === null || _b === void 0 ? void 0 : _b[name]) {
      return this.exposureData.exposures[name];
    }
    return null;
  };
  EgtnlBrowserDelegate.prototype.logExperiment = function (exposure) {
    var _a, _b;
    if ((_b = (_a = exposure === null || exposure === void 0 ? void 0 : exposure.evaluationData) === null || _a === void 0 ? void 0 : _a.experimentKey) === null || _b === void 0 ? void 0 : _b.experimentId) {
      this.evaluator().logExperiment(exposure.evaluationData, [this.context, this.pageData, exposure]);
    }
  };
  return EgtnlBrowserDelegate;
}();

// @ts-expect-error: no types declaration
var adaptAbacusLogger = function (logger, context, pageData) {
  return {
    logExperiment: function (exposure) {
      logger(context, pageData, exposure);
    }
  };
};
var getEGCSCallbacks = function (logger) {
  return {
    onFetchError: function (error) {
      logger.info('EGTNL_EGCS_FETCH_ERROR', "EGTNL EGCS failed on fetch. ".concat(error));
    },
    onInvalidPayload: function (error) {
      logger.info('EGTNL_EGCS_INVALID_PAYLOAD', "EGTNL EGCS failed due to invalid payload. ".concat(error));
    },
    onPublishError: function (error) {
      logger.info('EGTNL_EGCS_PUBLISH_ERROR', "EGTNL EGCS failed on publish. ".concat(error));
    }
  };
};
var getExperimentLoggers = function (logger) {
  var egcsCallbacks = getEGCSCallbacks(logger);
  var clickStreamLogger = EgTnlBucket.egLogger(egcsCallbacks);
  return [clickStreamLogger];
};
var initializeEgtnlBucket = function (exposureData, context, pageData, logger) {
  // this maps exposures by IDs instead of names
  var bucketData = Object.values(exposureData.exposures).map(function (e) {
    return e.evaluationData;
  }).filter(function (e) {
    return e;
  }).reduce(function (acc, evaluationData) {
    var _a;
    if ((_a = evaluationData === null || evaluationData === void 0 ? void 0 : evaluationData.experimentKey) === null || _a === void 0 ? void 0 : _a.experimentId) {
      acc[evaluationData.experimentKey.experimentId] = evaluationData;
    }
    return acc;
  }, {});
  EgTnlBucket.initialize(EgTnlBucket.bucketConfig(bucketData, getExperimentLoggers(logger)));
  return new EgtnlBrowserDelegate(EgTnlBucket.evaluator, context, pageData, exposureData);
};
var buildExperimentStore = function (exposureData, context, pageData, logger, egtnlEnabled, abacusEnabled) {
  var delegates = [];
  if (abacusEnabled) {
    var abacusLogger = adaptAbacusLogger(getExperimentLogger({
      logger: logger
    }), context, pageData);
    var abacusDelegate = new AbacusExperimentStoreDelegate(exposureData, abacusLogger);
    delegates.push(abacusDelegate);
  }
  if (egtnlEnabled) {
    var egtnlBrowserDelegate = initializeEgtnlBucket(exposureData, context, pageData, logger);
    delegates.push(egtnlBrowserDelegate);
  }
  return new BrowserExperimentStore(delegates);
};
var BrowserExperimentStore = /** @class */function () {
  function BrowserExperimentStore(delegates) {
    this.delegates = delegates;
    this.getExposure = this.getExposure.bind(this);
    this.logExperiment = this.logExperiment.bind(this);
  }
  BrowserExperimentStore.prototype.getExposure = function (name) {
    var result = this.delegates.reduce(function (currentExposure, delegate) {
      return delegate.getExposure(name);
    }, undefined);
    return result;
  };
  BrowserExperimentStore.prototype.logExperiment = function (exposure) {
    if (exposure) {
      this.delegates.forEach(function (d) {
        d.logExperiment(exposure);
      });
    }
  };
  return BrowserExperimentStore;
}();
var getExperimentContext = function (appData) {
  var experimentContextStore = appData.experimentContextStore;
  return experimentContextStore;
};
var getExperimentProvider = function (enableExperimentProvider, appData) {
  if (appData.experimentContextStore && enableExperimentProvider !== false) {
    // eslint-disable-next-line react/display-name
    return function (props) {
      return React.createElement(ExperimentProvider, {
        config: getExperimentContext(appData)
      }, props.children);
    };
  }
  return function (props) {
    return props.children;
  };
};
function ExperimentStorePlugin(pluginOptions) {
  var _a = pluginOptions.egtnlEnabled,
    egtnlEnabled = _a === void 0 ? false : _a,
    _b = pluginOptions.abacusEnabled,
    abacusEnabled = _b === void 0 ? true : _b,
    enableExperimentProvider = pluginOptions.enableExperimentProvider;
  return {
    name: 'experiment-store',
    onBrowserHydrateRenderContext: function (pluginContext, next) {
      var hydratedState = pluginContext.hydratedState,
        getAppData = pluginContext.getAppData,
        setAppData = pluginContext.setAppData;
      var appData = getAppData();
      var logger = appData.logger;
      var experimentStore = hydratedState.experimentStore;
      var experimentContextStore = buildExperimentStore(experimentStore, appData.context, appData.pageDataContext, logger, egtnlEnabled, abacusEnabled);
      setAppData({
        experimentContextStore: experimentContextStore
      });
      return next(pluginContext);
    },
    onBrowserWrapRootElement: function (pluginContext, next) {
      var getAppData = pluginContext.getAppData,
        setRootElement = pluginContext.setRootElement;
      var appData = getAppData();
      var ExperimentContextProvider = getExperimentProvider(enableExperimentProvider, appData);
      // eslint-disable-next-line react/display-name
      setRootElement(function (RootElement) {
        return function (props) {
          /**
           * Experiments running somewhere on bernie
           * Let's mark them for EGTNL manifest generator
           */
          useExperiment('1DL_EXPEDIA');
          useExperiment('SEO_CDN_Assets_Test');
          useExperiment('Bernie_SSR_ignore_analytics_context_response');
          useExperiment('Bernie_SVG_delivery');
          return React.createElement(ExperimentContextProvider, null, React.createElement(RootElement, null, props.children));
        };
      });
      return next(pluginContext);
    }
  };
}
export { ExperimentStorePlugin as default };